import type { QueryClient } from '@tanstack/react-query'
import { getPlanningCffsApi } from 'api-validations/locutus'
import { ApiError } from 'utils'

import { useOrgCodeOrThrow } from '../active-operation'
import { type ApiClient, panoramaQueryOptions, usePanoramaQuery } from '../api-provider'
import { type Cff, sameCff } from '../operation-wide-data/browsableFields'

/**
 * Resolves a planning field id back to its (client, farm, field) names. Used
 * when a plan asset is the active selection (it carries only the planning field
 * id) and the sidebar needs the field's CFF to scope the as-applied task list.
 * Undefined while loading or if the id is unknown.
 */
export const useCffForPlanningField = (planningFieldId: string | undefined): Cff | undefined => {
  const orgCode = useOrgCodeOrThrow()

  const query = usePanoramaQuery(
    getPlanningCffsApi,
    { orgCode },
    {
      enabled: planningFieldId !== undefined,
      select: ({ items }) => {
        const match = items.find((cff) => cff.id === planningFieldId)
        return match === undefined
          ? undefined
          : { client: match.client, farm: match.farm, field: match.field }
      }
    }
  )

  return query.data
}

export type PlanningFieldLookup = {
  /** The planning field id, or undefined when the field isn't registered for planning. */
  id: string | undefined
  /** True while the CFF list is loading — `id` is not yet meaningful. */
  isLoading: boolean
}

/**
 * Resolves a field's planning-field id from its (client, farm, field) names —
 * the inverse of `useCffForPlanningField`. Planning uses its own UUID per field
 * grouping, distinct from a Stream's CFF tag; the picker needs this id to
 * address the field's Plans and plan assets. `isLoading` separates "still
 * fetching" from "not registered for planning" so callers can offer the
 * add-to-planning affordance only once the answer is known.
 */
export const usePlanningFieldLookup = (cff: Cff | undefined): PlanningFieldLookup => {
  const orgCode = useOrgCodeOrThrow()

  const query = usePanoramaQuery(
    getPlanningCffsApi,
    { orgCode },
    {
      enabled: cff !== undefined && cff.field.trim() !== '',
      select: ({ items }) =>
        cff === undefined ? undefined : items.find((planningCff) => sameCff(planningCff, cff))?.id
    }
  )

  return { id: query.data, isLoading: query.isLoading }
}

/** `usePlanningFieldLookup` without the loading flag, for callers that only need the id. */
export const usePlanningFieldId = (cff: Cff | undefined): string | undefined =>
  usePlanningFieldLookup(cff).id

// Locutus lists CFFs with an eventually consistent Query, so a GET right after
// the create's 204 can miss the row just written.
const newFieldRetryDelaysMs = [0, 400, 1000]

/**
 * Resolves the id Locutus assigned to a CFF just registered for planning. The
 * create returns 204 with no body, so the CFF list is re-read and the new row
 * matched with `sameCff`, on a short retry schedule while the list catches up.
 * A hit also primes the list's cache entry, so `usePlanningFieldLookup`
 * consumers see the new row without waiting for the invalidation refetch.
 * Returns undefined when the row never appeared. Client errors propagate
 * rather than retry.
 */
export const resolveNewPlanningFieldId = async ({
  apiClient,
  queryClient,
  orgCode,
  cff
}: {
  apiClient: ApiClient
  queryClient: QueryClient
  orgCode: string
  cff: Cff
}): Promise<string | undefined> => {
  for (const wait of newFieldRetryDelaysMs) {
    if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait))
    try {
      const { items } = await apiClient.callApi(getPlanningCffsApi, { orgCode })
      const match = items.find((item) => sameCff(item, cff))
      if (match !== undefined) {
        queryClient.setQueryData(
          panoramaQueryOptions(getPlanningCffsApi, { apiClient, params: { orgCode } }).queryKey,
          { items }
        )
        return match.id
      }
    } catch (error) {
      if (error instanceof ApiError && error.isClientError()) throw error
    }
  }
  return undefined
}
